import {
  Container,
  Card,
  Stack,
  Text,
  Title,
  Group,
  Badge,
  Loader,
  Alert,
  Button,
  Progress,
  SimpleGrid,
  Grid,
  Box,
  ThemeIcon,
  Paper,
  ScrollArea,
  UnstyledButton,
} from '@mantine/core';
import {
  IconExclamationCircle,
  IconCurrencyDollar,
  IconUsers,
  IconEye,
  IconShoppingBag,
  IconChevronRight,
  IconChartBar,
  IconBell,
  IconPackage,
  IconMessage,
  IconCrown,
  IconPhoto,
  IconBuildingStore,
  IconCategory,
  IconSettings,
  IconLock,
  IconSparkles,
  IconTrendingUp,
  IconAlbum,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../api';
import SellerStats from './SellerStats';
import SellerProfileSettings from './SellerProfileSettings';
import BoostCard from './BoostCard';

// ── Simple SVG bar chart ─────────────────────────────────────────────────────
const WeeklyEarningsChart = ({ data = [] }) => {
  const PAD = { top: 8, right: 8, bottom: 28, left: 38 };
  const W = 420;
  const H = 160;
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const maxVal = Math.max(1, ...data.map(d => d.value));
  const yTicks = [0, 0.2, 0.4, 0.6, 0.8, 1.0];
  const barW = data.length ? chartW / (data.length * 2) : 30;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {/* Y gridlines + labels */}
      {yTicks.map((tick, i) => {
        const y = PAD.top + chartH - (tick / maxVal) * chartH;
        return (
          <g key={i}>
            <text x={PAD.left - 4} y={y + 3.5} textAnchor="end" fontSize={9} fill="#868e96">
              ${tick}
            </text>
            <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
              stroke="#f1f3f5" strokeWidth={tick === 0 ? 1.5 : 1} />
          </g>
        );
      })}

      {/* Bars */}
      {data.map((d, i) => {
        const bh = Math.max(2, (d.value / maxVal) * chartH);
        const x = PAD.left + i * (chartW / data.length) + barW * 0.5;
        const y = PAD.top + chartH - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} rx={2} fill="#9f38cc" opacity={0.85} />
            <text x={x + barW / 2} y={H - 6} textAnchor="middle" fontSize={8} fill="#868e96">
              {d.label}
            </text>
          </g>
        );
      })}

      {/* Legend */}
      <rect x={PAD.left} y={2} width={10} height={8} rx={1} fill="#9f38cc" />
      <text x={PAD.left + 14} y={10} fontSize={9} fill="#495057">Weekly Earnings ($)</text>
    </svg>
  );
};

// ── Bottom nav tabs ──────────────────────────────────────────────────────────
const NAV_TABS = [
  { label: 'My Content',     icon: <IconPhoto size="0.9rem" />,         path: '/seller/products' },
  { label: 'Albums',         icon: <IconAlbum size="0.9rem" />,         path: '/seller/albums' },
  { label: 'Orders',         icon: <IconPackage size="0.9rem" />,       path: '/seller/orders' },
  { label: 'Buyers',         icon: <IconUsers size="0.9rem" />,         path: '/seller/buyers' },
  { label: 'Earnings',       icon: <IconCurrencyDollar size="0.9rem" />, path: '/seller/earnings' },
  { label: 'Messages',       icon: <IconMessage size="0.9rem" />,       path: '/messages' },
  { label: 'Subscriptions',  icon: <IconCrown size="0.9rem" />,         path: '/subscription' },
  { label: 'Vault',          icon: <IconLock size="0.9rem" />,          path: '/seller/vault' },
  { label: 'Manager',        icon: <IconSettings size="0.9rem" />,      path: '/seller/dashboard' },
  { label: 'Notifications',  icon: <IconBell size="0.9rem" />,          path: '/seller/notifications' },
  { label: 'Sellers',        icon: <IconBuildingStore size="0.9rem" />, path: '/princesses' },
  { label: 'Categories',     icon: <IconCategory size="0.9rem" />,      path: '/browse-categories' },
  { label: 'Analytics',      icon: <IconTrendingUp size="0.9rem" />,    path: '/seller/analytics' },
];

const BottomNavTabs = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box style={{
      borderTop: '1px solid var(--mantine-color-gray-2)',
      background: '#fff',
      paddingTop: 4,
      paddingBottom: 4,
    }}>
      <ScrollArea scrollbarSize={0}>
        <Group gap={0} wrap="nowrap" style={{ minWidth: 'max-content', padding: '0 8px' }}>
          {NAV_TABS.map((tab) => {
            const isActive = location.pathname === tab.path;
            return (
              <UnstyledButton
                key={tab.label}
                onClick={() => navigate(tab.path)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  padding: '6px 12px',
                  borderBottom: isActive ? '2px solid #9f38cc' : '2px solid transparent',
                  color: isActive ? '#9f38cc' : '#868e96',
                  fontSize: 11,
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s',
                }}
              >
                {tab.icon}
                {tab.label}
              </UnstyledButton>
            );
          })}
        </Group>
      </ScrollArea>
    </Box>
  );
};

// ── Stat box ────────────────────────────────────────────────────────────────
const StatBox = ({ label, value, icon, onClick }) => (
  <UnstyledButton onClick={onClick} style={{ width: '100%' }}>
    <Paper withBorder p="md" radius="md" style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="xs" c="dimmed">{label}</Text>
          <Text size="xl" fw={700}>{value}</Text>
        </Stack>
        <Group gap={4} align="center">
          <ThemeIcon size="sm" color="grape" variant="light" radius="xl">
            {icon}
          </ThemeIcon>
          {onClick && <IconChevronRight size="0.85rem" color="var(--mantine-color-gray-5)" />}
        </Group>
      </Group>
    </Paper>
  </UnstyledButton>
);

// ── Profile completion ───────────────────────────────────────────────────────
const getProfileCompletion = (profile) => {
  const checks = [
    !!profile?.business_name,
    !!profile?.business_description,
    !!profile?.business_city,
    !!profile?.business_phone,
    !!profile?.is_verified,
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
};

// ── Main component ───────────────────────────────────────────────────────────
const SellerDashboard = () => {
  const navigate = useNavigate();
  const { data: sellerProfile, loading, error } = useApi('/api/v1/me/seller');

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  if (error && error.code !== 'forbidden') {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red" title="Error loading dashboard">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (!sellerProfile) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconExclamationCircle size="1rem" />} color="pink" title="No princess profile found">
          You need to register as a princess first to access this dashboard.
        </Alert>
      </Container>
    );
  }

  const profilePct = getProfileCompletion(sellerProfile);
  const totalSalesDollars = (sellerProfile.total_sales_cents ?? 0) / 100;

  // Sample weekly data (will be replaced with real data from backend)
  const weeklyData = [
    { label: '18/02', value: 0 },
    { label: '20/02', value: 0 },
    { label: '21/02', value: 0 },
    { label: '22/02', value: 0 },
    { label: '25/02', value: 0 },
    { label: '26/02', value: 0 },
    { label: '27/02', value: 0 },
  ];

  return (
    <Stack gap={0}>
      <Container size="lg" py="lg" style={{ width: '100%' }}>
        <Stack gap="lg">

          {/* Stats row */}
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            <StatBox
              label="Earnings"
              value={`$${totalSalesDollars.toFixed(2)}`}
              icon={<IconCurrencyDollar size="0.8rem" />}
              onClick={() => navigate('/seller/earnings')}
            />
            <StatBox
              label="Subscribers"
              value={sellerProfile.subscriber_count ?? 0}
              icon={<IconUsers size="0.8rem" />}
              onClick={() => navigate('/seller/buyers')}
            />
            <StatBox
              label="Profile Views"
              value={sellerProfile.profile_views ?? 0}
              icon={<IconEye size="0.8rem" />}
            />
            <StatBox
              label="Sales"
              value={sellerProfile.total_orders ?? 0}
              icon={<IconShoppingBag size="0.8rem" />}
              onClick={() => navigate('/seller/orders')}
            />
          </SimpleGrid>

          {/* Analytics (contract §4.12) */}
          <SellerStats />

          {/* Profile setup banner */}
          {profilePct < 100 && (
            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" align="center" wrap="nowrap">
                <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6}>
                    <Text size="sm" fw={500}>Finish your profile setup</Text>
                    <Text size="sm" c="grape" fw={700}>{profilePct}%</Text>
                  </Group>
                  <Progress value={profilePct} color="grape" size="sm" radius="xl" />
                  <Text size="xs" c="dimmed">Complete the next steps to start selling.</Text>
                </Stack>
                <Button
                  color="grape"
                  size="sm"
                  ml="md"
                  onClick={() => navigate('/build-profile')}
                  style={{ flexShrink: 0 }}
                >
                  Complete profile
                </Button>
              </Group>
            </Paper>
          )}

          {/* Status alert */}
          {sellerProfile.seller_status === 'pending' && (
            <Alert color="yellow" icon={<IconExclamationCircle size="1rem" />}>
              Your princess account is pending approval. You'll be able to list products once approved.
            </Alert>
          )}
          {sellerProfile.seller_status === 'rejected' && (
            <Alert color="red" icon={<IconExclamationCircle size="1rem" />}>
              Your princess application was rejected. Please contact support for more information.
            </Alert>
          )}
          {sellerProfile.seller_status === 'suspended' && (
            <Alert color="orange" icon={<IconExclamationCircle size="1rem" />}>
              Your princess account is suspended. Please contact support for assistance.
            </Alert>
          )}

          {/* Weekly Earnings + Recent Notifications */}
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Card withBorder radius="md" padding="md" style={{ height: '100%' }}>
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Group gap="xs">
                      <ThemeIcon size="sm" color="grape" variant="light" radius="xl">
                        <IconChartBar size="0.8rem" />
                      </ThemeIcon>
                      <Text fw={600} size="sm">Weekly Earnings</Text>
                    </Group>
                    <Badge size="xs" color="grape" variant="light">
                      ${totalSalesDollars.toFixed(2)} total
                    </Badge>
                  </Group>
                  <WeeklyEarningsChart data={weeklyData} />
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 5 }}>
              <Card withBorder radius="md" padding="md" style={{ height: '100%', minHeight: 220 }}>
                <Stack gap="sm">
                  <Group gap="xs">
                    <ThemeIcon size="sm" color="pink" variant="light" radius="xl">
                      <IconBell size="0.8rem" />
                    </ThemeIcon>
                    <Text fw={600} size="sm">Recent Notifications</Text>
                  </Group>
                  <Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 32 }}>
                    <Stack align="center" gap="xs">
                      <ThemeIcon size={40} radius="xl" color="gray" variant="light">
                        <IconBell size="1.2rem" />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed" ta="center">No notifications yet</Text>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>

          {/* Quick actions */}
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
            <Button
              variant="light"
              color="grape"
              leftSection={<IconPackage size="1rem" />}
              onClick={() => navigate('/seller/products')}
              justify="flex-start"
            >
              My Products
            </Button>
            <Button
              variant="light"
              color="pink"
              leftSection={<IconSparkles size="1rem" />}
              onClick={() => navigate('/seller/special-requests')}
              justify="flex-start"
            >
              Special Requests
            </Button>
            <Button
              variant="light"
              color="blue"
              leftSection={<IconPhoto size="1rem" />}
              onClick={() => navigate('/seller/media')}
              justify="flex-start"
            >
              My Gallery
            </Button>
            <Button
              variant="light"
              color="violet"
              leftSection={<IconAlbum size="1rem" />}
              onClick={() => navigate('/seller/albums')}
              justify="flex-start"
            >
              My Albums
            </Button>
          </SimpleGrid>

          {/* Profile boost / featured placement (contract §4.21) — the boost
              routes are PrincessUser()-gated, so approved + verified only */}
          {sellerProfile.seller_status === 'approved' && sellerProfile.is_verified && (
            <BoostCard />
          )}

          {/* Public profile extras: banner, links, shoe size (contract §3.5) */}
          <SellerProfileSettings />

        </Stack>
      </Container>

      {/* Bottom navigation strip */}
      <BottomNavTabs />
    </Stack>
  );
};

export default SellerDashboard;
